import React, { useEffect, useState } from 'react'
import { Trash2, Plus } from 'lucide-react'
import { AppLayout } from '../../layouts/AppLayout'
import { PageHeader } from '../../layouts/PageHeader'
import { useUserSession } from '../../../context/UserSessionContext'
import {
  fetchAllowedUsers,
  saveAllowedUsers,
  type AllowedUserRow,
} from '../../../utils/allowedEmailsApi'
import { VALID_ROLES, formatRoleDisplay } from '../../../config/appRoles'

export const EmailAccessPage: React.FC = () => {
  const { isAdmin, googleUser, logAudit } = useUserSession()
  const [users, setUsers] = useState<AllowedUserRow[]>([])
  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState('finance')
  const [error, setError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setLoading(true)
    fetchAllowedUsers()
      .then((list: AllowedUserRow[]) => setUsers(list))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false))
  }, [])

  const normalized = (s: string) => s.trim().toLowerCase()

  const handleAdd = () => {
    const email = normalized(newEmail)
    setError(null)
    if (!email) {
      setError('Enter a valid email address.')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.')
      return
    }
    if (users.some(u => u.email === email)) {
      setError('This email is already in the list.')
      return
    }
    const role = VALID_ROLES.includes(newRole) ? newRole : 'finance'
    setUsers(prev =>
      [...prev, { email, role }].sort((a, b) => a.email.localeCompare(b.email))
    )
    setNewEmail('')
  }

  const handleRemove = (emailToRemove: string) => {
    setUsers(prev => prev.filter(u => u.email !== emailToRemove))
    setError(null)
  }

  const handleRoleChange = (email: string, newRoleValue: string) => {
    const role = VALID_ROLES.includes(newRoleValue) ? newRoleValue : 'finance'
    setUsers(prev =>
      prev.map(u => (u.email === email ? { ...u, role } : u))
    )
    setError(null)
  }

  const handleSave = async () => {
    setError(null)
    setSaving(true)
    try {
      await saveAllowedUsers(users, googleUser?.email)
      logAudit('Email access list saved', `${users.length} users`)
      setSaveSuccess(true)
      window.setTimeout(() => setSaveSuccess(false), 2400)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save. Try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, action: 'add') => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (action === 'add') handleAdd()
    }
  }

  if (!isAdmin) {
    return (
      <AppLayout>
        <PageHeader
          title="Email access & roles"
          subtitle="Manage sign-in whitelist and RBAC roles."
        />
        <div className="p-6 max-w-lg">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-950">
            <p className="font-semibold">Access denied</p>
            <p className="mt-1 text-amber-900/90">
              Only administrators can edit the allowed-email list. Use the
              classic app at <a href="/" className="underline font-medium">/</a>{' '}
              if you need a different account.
            </p>
          </div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <PageHeader
        title="Email access & roles"
        subtitle="Who may sign in and their role (Finance, RMG, Admin). Stored in server config."
      />
      <div className="p-6 max-w-2xl space-y-4">
        {saveSuccess && (
          <p className="text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
            Saved. Access list updated.
          </p>
        )}
        {error && (
          <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-2">
            {error}
          </p>
        )}

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
          <div className="flex gap-2 flex-wrap">
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => handleKeyDown(e, 'add')}
              placeholder="Add email address"
              className="flex-1 min-w-[160px] px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={newRole}
              onChange={e => setNewRole(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Role for new user"
            >
              {VALID_ROLES.map((r: string) => (
                <option key={r} value={r}>
                  {formatRoleDisplay(r)}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600">
              Users with access ({users.length})
            </div>
            <ul className="max-h-[min(420px,50vh)] overflow-auto divide-y divide-slate-100">
              {loading ? (
                <li className="px-3 py-8 text-sm text-slate-500 text-center">
                  Loading…
                </li>
              ) : users.length === 0 ? (
                <li className="px-3 py-8 text-sm text-slate-500 text-center">
                  No users in the list yet. When empty, the server may allow all
                  sign-ins — check your deployment policy.
                </li>
              ) : (
                users.map(({ email, role }) => (
                  <li
                    key={email}
                    className="flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-slate-50/80"
                  >
                    <span className="truncate flex-1 min-w-0 text-sm text-slate-800">
                      {email}
                    </span>
                    <select
                      value={role}
                      onChange={e => handleRoleChange(email, e.target.value)}
                      className="shrink-0 px-2 py-1 text-xs rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      title={`Role for ${email}`}
                    >
                      {VALID_ROLES.map((r: string) => (
                        <option key={r} value={r}>
                          {formatRoleDisplay(r)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => handleRemove(email)}
                      className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save to server'}
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

export default EmailAccessPage
